import { useCallback } from "react";
import { v4 as uuid } from "uuid";
import { usePaths } from "BEDROCK/paths";
import { useProjectStore } from "entity-store/project-store/use-project-store";
import { useGpsStoreLocal } from "entity-store/gps-store/use-gps-store-local";
import { useStravaPreviewData } from "./use-strava-preview-data";
import { ProjectTemplate, applyTemplateToProject } from "PosterEditor/model/project-template";
import { GPSSource, defaultCoordinateLimit } from "PosterEditor/model/poster-project";
import { defaultGPSSource } from "PosterEditor/model/default-project-style";
import { reduceCoordinates } from "PosterEditor/hooks/use-coordinates";

/**
 * Hook for creating a project from a template, optionally with Strava data.
 *
 * This consolidates the logic for:
 * 1. Checking if Strava preview data exists
 * 2. Creating a project from a template
 * 3. Applying Strava GPS data and metadata if available
 * 4. Saving the project and navigating to the editor
 *
 * Used by collection-list-page and collection-page to avoid duplicated logic.
 */
export const useCreateProjectWithStrava = () => {
  const { navigateTo, mapEditor } = usePaths();
  const { draftProject, saveProject } = useProjectStore();
  const { hasStravaData, getPreviewData, clearPreviewData } = useStravaPreviewData();
  const { saveCoordinates } = useGpsStoreLocal();

  /**
   * Create a project from a template.
   * If Strava preview data exists, it will be applied to the project.
   * Navigates to the editor after creation.
   *
   * @param template - The template to apply to the new project
   */
  const createProjectWithTemplate = useCallback(async (template: ProjectTemplate): Promise<void> => {
    if (hasStravaData()) {
      // Strava flow: create a project with template and GPS data (works for all users)
      const previewData = getPreviewData();
      if (!previewData) {
        // Fallback to normal flow if preview data is missing
        const project = await draftProject(false, template);
        applyTemplateToProject(template, project);
        navigateTo(mapEditor + "/" + project.id);
        return;
      }

      // Create project with template
      const project = await draftProject(false, template);
      applyTemplateToProject(template, project);

      // Create GPS source from Strava preview data
      const gpsSourceId = uuid();
      const displayedCoordinates = reduceCoordinates(previewData.coordinates, defaultCoordinateLimit);

      const gpsSource: GPSSource = {
        ...defaultGPSSource,
        id: gpsSourceId,
        name: previewData.activityName,
        originDescription: `Strava: ${previewData.activityType}`,
        allCoordinates: previewData.coordinates,
        displayedCoordinates,
        data: previewData.gpsData,
      };

      // Save GPS source to IndexedDB
      await saveCoordinates(gpsSource, gpsSourceId);

      // Update project with GPS data
      project.name = previewData.activityName;
      project.gpsSourceIDs = [gpsSourceId];
      project.gpsData = {
        id: gpsSourceId,
        data: previewData.gpsData,
      };
      project.bounds = undefined; // Clear bounds so map auto-fits to GPS data

      // Save Strava metadata for label population and toolbox badges
      // Use Strava API values (more accurate than GPS-calculated values)
      if (previewData.activityId && previewData.gpsData) {
        project.stravaMetadata = {
          activityId: previewData.activityId,
          activityName: previewData.activityName,
          activityType: previewData.activityType,
          startDate: previewData.startDate,
          distance: previewData.stravaDistance,
          elevationGain: previewData.stravaElevationGain,
          movingTime: previewData.stravaMovingTime,
          athleteFirstName: previewData.athleteFirstName,
          importedAt: Date.now(),
        };
      }

      // Save the updated project with GPS data to localStorage
      saveProject(project);

      // Clear the preview data since we've consumed it
      clearPreviewData();

      // Navigate to editor with the styled project
      navigateTo(mapEditor + "/" + project.id);
      return;
    }

    // Normal flow: just create a project from template
    const project = await draftProject(false, template);
    applyTemplateToProject(template, project);

    navigateTo(mapEditor + "/" + project.id);
  }, [
    hasStravaData,
    getPreviewData,
    clearPreviewData,
    draftProject,
    saveProject,
    saveCoordinates,
    navigateTo,
    mapEditor,
  ]);

  return {
    createProjectWithTemplate,
    hasStravaData,
  };
};
